"use client";

/**
 * The composer's left-hand inline controls: the "/" slash affordance and the
 * controls whose visibility ChatInputActions decides through
 * `isControlVisible(key, permissionAllows)`. Each control renders nothing when
 * it is not visible; hiding one never changes its behaviour or default.
 */

import { Button, Tooltip, TooltipTrigger, TooltipContent, Switch } from "@heroui/react";
import { Icon } from "@iconify/react";
import { cn } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { blurActiveElement } from "@/utils/dom";

interface SlashAffordanceProps {
  commandMenuVisible: boolean;
  onCommandMenuOpen: () => void;
}

/** "/" slash affordance — opens the command menu. */
export const SlashAffordance = ({
  commandMenuVisible,
  onCommandMenuOpen,
}: SlashAffordanceProps) => {
  const { t } = useTranslation();

  return (
    <button
      aria-expanded={commandMenuVisible}
      aria-haspopup="menu"
      aria-label={t("chatInput.commandMenu.open")}
      className="inline-flex items-center justify-center w-7 h-7 rounded-md text-default-400 hover:text-default-600 hover:bg-default-100 transition-colors text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      data-id="slash-affordance-button"
      type="button"
      onClick={onCommandMenuOpen}
    >
      /
    </button>
  );
};

interface WebSearchControlProps {
  visible: boolean;
  isSearchEnabled: boolean;
  onWebSearchToggle: (val: boolean) => void;
}

/** Web Search switch. */
export const WebSearchControl = ({
  visible,
  isSearchEnabled,
  onWebSearchToggle,
}: WebSearchControlProps) => {
  const { t } = useTranslation();

  if (!visible) return null;

  return (
    <Tooltip>
      <TooltipTrigger>
        <div className="flex items-center gap-2 py-1 px-2">
          <Icon className="text-default-500" icon="solar:global-linear" width={18} />
          <span className="text-tiny text-default-500">{t("Web Search")}</span>
          <Switch
            aria-label={t("Web Search")}
            color={isSearchEnabled ? "success" : "default"}
            isSelected={isSearchEnabled}
            size="sm"
            onChange={onWebSearchToggle}
          >
            <Switch.Content>
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
            </Switch.Content>
          </Switch>
        </div>
      </TooltipTrigger>
      <TooltipContent showArrow>
        {isSearchEnabled ? t("Disable web search") : t("Enable web search")}
      </TooltipContent>
    </Tooltip>
  );
};

interface KnowledgeHubControlProps {
  visible: boolean;
  isKnowledgeHubEnabled: boolean;
  onKnowledgeHubToggle: () => void;
}

/** Knowledge Hub toggle. */
export const KnowledgeHubControl = ({
  visible,
  isKnowledgeHubEnabled,
  onKnowledgeHubToggle,
}: KnowledgeHubControlProps) => {
  const { t } = useTranslation();

  if (!visible) return null;

  const handlePress = () => {
    onKnowledgeHubToggle();
    setTimeout(() => {
      blurActiveElement();
    }, 0);
  };

  return (
    <Tooltip>
      <TooltipTrigger>
        <Button
          aria-label={t("Knowledge Hub")}
          aria-pressed={isKnowledgeHubEnabled}
          className={cn(
            "shrink-0 min-w-fit rounded-full border!",
            isKnowledgeHubEnabled
              ? "bg-primary-100 hover:bg-primary-200 border-primary-500! text-primary-700"
              : "bg-default-100 hover:bg-default-200 border-default-300!",
          )}
          data-id="knowledge-hub-button"
          size="sm"
          variant="ghost"
          onPress={handlePress}
        >
          <Icon
            className={isKnowledgeHubEnabled ? "text-primary-700" : "text-default-500"}
            icon="solar:database-linear"
            width={18}
          />
          <span className="hidden sm:inline">{t("Knowledge Hub")}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent showArrow>
        {isKnowledgeHubEnabled ? t("Disable knowledge hub") : t("Enable knowledge hub")}
      </TooltipContent>
    </Tooltip>
  );
};

interface PromptsControlProps {
  visible: boolean;
  isPromptLocked: boolean;
  onBrowsePrompts: () => void;
}

/** Browse prompts. */
export const PromptsControl = ({
  visible,
  isPromptLocked,
  onBrowsePrompts,
}: PromptsControlProps) => {
  const { t } = useTranslation();

  if (!visible) return null;

  const handlePress = () => {
    if (!isPromptLocked) {
      onBrowsePrompts();
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger>
        <Button
          aria-label={t("Prompts")}
          className="shrink-0 min-w-fit bg-default-100 hover:bg-default-200 border! border-default-300! rounded-full disabled:bg-default-200 disabled:text-default-400 disabled:cursor-not-allowed"
          data-id="prompts-button"
          isDisabled={isPromptLocked}
          size="sm"
          variant="ghost"
          onPress={handlePress}
        >
          <Icon
            className={isPromptLocked ? "text-default-400" : "text-default-500"}
            icon="solar:code-bold"
            width={18}
          />
          <span className="hidden sm:inline">{t("Prompts")}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent showArrow>
        {isPromptLocked
          ? t("Prompts disabled for this workflow")
          : t("Browse and select from saved prompts")}
      </TooltipContent>
    </Tooltip>
  );
};

interface PromptoControlProps {
  visible: boolean;
  onPrompto: () => void;
}

/** Prompto. */
export const PromptoControl = ({ visible, onPrompto }: PromptoControlProps) => {
  const { t } = useTranslation();

  if (!visible) return null;

  return (
    <Tooltip>
      <TooltipTrigger>
        <Button
          aria-label={t("Prompto")}
          className="shrink-0 min-w-fit bg-default-100 hover:bg-default-200 border! border-default-300! rounded-full"
          data-id="prompto-button"
          size="sm"
          variant="ghost"
          onPress={onPrompto}
        >
          <Icon className="shrink-0" icon="solar:magic-stick-3-bold" width={16} />
          <span className="hidden sm:inline">{t("Prompto")}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent showArrow>{t("Generate effective prompts with AI")}</TooltipContent>
    </Tooltip>
  );
};

interface ConfigurationControlProps {
  visible: boolean;
  isConfigurationDisabled: boolean;
  onOpenConfiguration: () => void;
}

/** Configuration. */
export const ConfigurationControl = ({
  visible,
  isConfigurationDisabled,
  onOpenConfiguration,
}: ConfigurationControlProps) => {
  const { t } = useTranslation();

  if (!visible) return null;

  const handlePress = () => {
    if (!isConfigurationDisabled) {
      onOpenConfiguration();
      setTimeout(() => {
        blurActiveElement();
      }, 0);
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger>
        <Button
          aria-label={t("Configuration")}
          className="shrink-0 min-w-fit bg-default-100 hover:bg-default-200 border! border-default-300! rounded-full disabled:bg-default-200 disabled:text-default-400 disabled:cursor-not-allowed"
          data-id="configuration-button"
          isDisabled={isConfigurationDisabled}
          size="sm"
          variant="ghost"
          onPress={handlePress}
        >
          <Icon
            className={isConfigurationDisabled ? "text-default-400" : "text-default-500"}
            icon="solar:settings-linear"
            width={18}
          />
          <span className="hidden sm:inline">{t("Configuration")}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent showArrow>
        {isConfigurationDisabled
          ? t("disabled-model-selector")
          : t("Open chat configuration settings")}
      </TooltipContent>
    </Tooltip>
  );
};
